import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { uploadTrackingIds } from '../../api/uploadTracking';
import {
  EmptyState,
  PageHeader,
  StatusBanner,
  inputClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
} from '../../components/ui';

const OVERRIDE_PREF_KEY = 'crm_upload_tracking_override_pref';
const PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [50, 100, 200];

function readOverridePref() {
  try {
    const value = localStorage.getItem(OVERRIDE_PREF_KEY);
    if (value === 'always' || value === 'never') return value;
  } catch {
    // ignore
  }
  return null;
}

function writeOverridePref(value) {
  try {
    if (!value) localStorage.removeItem(OVERRIDE_PREF_KEY);
    else localStorage.setItem(OVERRIDE_PREF_KEY, value);
  } catch {
    // ignore
  }
}

const ORDER_ALIASES = [
  'order id',
  'orderid',
  'amazon order id',
  'amazon order',
  'amazonorderid',
  'order number',
  'order no',
  'us order id',
  'us order',
];

const TRACKING_ALIASES = [
  'carrier tracking #',
  'carrier tracking',
  'carrier tracking number',
  'tracking id',
  'tracking',
  'tracking number',
  'tracking no',
  'tracking #',
  'us tracking id',
  'us tracking',
  'awb',
];

const CARRIER_ALIASES = [
  'carrier name',
  'carrier',
  'buy carrier name',
  'shipping carrier',
];

const ASIN_ALIASES = ['asin', 'asin in', 'asin com', 'product asin'];

const QUANTITY_ALIASES = ['item quantity', 'item qty'];

const PRICE_ALIASES = ['purchase ppu', 'purchase price'];

const TITLE_ALIASES = ['title', 'product title', 'item title', 'product name'];

function normalizeHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function cleanCell(value) {
  let text = String(value ?? '').trim();
  if (/^=+"/.test(text) && text.endsWith('"')) {
    text = text.replace(/^=+"/, '').replace(/"$/, '');
  } else if (text.startsWith('=')) {
    text = text.slice(1);
  }
  return text.replace(/^"+|"+$/g, '').trim();
}

function normalizeUsOrderId(value) {
  return cleanCell(value)
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
    .replace(/^order\s*(id|number|no)?\s*[:=#-]?\s*/i, '')
    .replace(/\s+/g, '');
}

function parseItemQuantity(value) {
  const raw = cleanCell(value).replace(/,/g, '');
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
}

function parsePurchasePrice(value) {
  const raw = cleanCell(value).replace(/[$,]/g, '');
  if (!raw || /^n\/?a$/i.test(raw)) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

function detectColumns(headers) {
  const mapping = {
    usOrderId: null,
    trackingNumber: null,
    carrierName: null,
    asin: null,
    itemQuantity: null,
    purchasePrice: null,
    title: null,
  };

  // Prefer the exact Amazon Business "Order ID" header before looser aliases.
  for (const header of headers) {
    if (normalizeHeader(header) === 'order id') {
      mapping.usOrderId = header;
      break;
    }
  }

  for (const header of headers) {
    const key = normalizeHeader(header);
    if (!mapping.usOrderId && ORDER_ALIASES.includes(key)) {
      mapping.usOrderId = header;
    }
    if (!mapping.trackingNumber && TRACKING_ALIASES.includes(key)) {
      mapping.trackingNumber = header;
    }
    if (!mapping.carrierName && CARRIER_ALIASES.includes(key)) {
      mapping.carrierName = header;
    }
    if (!mapping.asin && ASIN_ALIASES.includes(key)) {
      mapping.asin = header;
    }
    if (!mapping.itemQuantity && QUANTITY_ALIASES.includes(key)) {
      mapping.itemQuantity = header;
    }
    if (!mapping.purchasePrice && PRICE_ALIASES.includes(key)) {
      mapping.purchasePrice = header;
    }
    if (!mapping.title && TITLE_ALIASES.includes(key)) {
      mapping.title = header;
    }
  }
  return mapping;
}

function productKey(row) {
  return `${String(row.usOrderId || '').trim().toLowerCase()}|${String(row.asin || '').trim().toUpperCase()}`;
}

function groupProductRows(rows) {
  const map = new Map();
  for (const row of rows) {
    const usOrderId = normalizeUsOrderId(row.usOrderId);
    if (!usOrderId) continue;
    const key = productKey({ ...row, usOrderId });
    let group = map.get(key);
    if (!group) {
      group = {
        usOrderId,
        asin: String(row.asin || '').trim().toUpperCase(),
        sourceRows: [],
        trackingNumbers: [],
        carrierName: '',
        itemQuantity: 0,
        quantityComplete: true,
      };
      map.set(key, group);
    }
    group.sourceRows.push({
      usOrderId,
      asin: group.asin,
      trackingNumber: String(row.trackingNumber || '').trim(),
      carrierName: String(row.carrierName || '').trim(),
      itemQuantity: row.itemQuantity ?? null,
      purchasePrice: row.purchasePrice ?? null,
      title: String(row.title || '').trim(),
    });
    const trackingNumber = String(row.trackingNumber || '').trim();
    if (trackingNumber && !group.trackingNumbers.includes(trackingNumber)) {
      group.trackingNumbers.push(trackingNumber);
    }
    if (!group.carrierName && row.carrierName) {
      group.carrierName = String(row.carrierName).trim();
    }
    if (row.itemQuantity == null) {
      group.quantityComplete = false;
    } else {
      group.itemQuantity += row.itemQuantity;
    }
  }

  return [...map.values()].map((group) => ({
    ...group,
    trackingNumber: group.trackingNumbers.join(', '),
  }));
}

function rowsForApi(groups) {
  return groups.flatMap((group) => group.sourceRows || []);
}

function rowsFromSheet(sheet) {
  const json = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
  if (!json.length) return [];
  const headers = Object.keys(json[0] || {});
  const columns = detectColumns(headers);

  if (!columns.usOrderId || !columns.trackingNumber || !columns.asin || !columns.itemQuantity) {
    throw new Error(
      `Excel must include “Order ID”, “Carrier Tracking #”, “ASIN”, and “Item Quantity” columns (Amazon Business order report format). Found: ${headers.slice(0, 12).join(', ') || 'no headers'}.`
    );
  }

  const rows = json
    .map((row) => ({
      usOrderId: normalizeUsOrderId(row[columns.usOrderId]),
      trackingNumber: cleanCell(row[columns.trackingNumber]),
      carrierName: columns.carrierName ? cleanCell(row[columns.carrierName]) : '',
      asin: cleanCell(row[columns.asin]).toUpperCase(),
      itemQuantity: parseItemQuantity(row[columns.itemQuantity]),
      purchasePrice: columns.purchasePrice ? parsePurchasePrice(row[columns.purchasePrice]) : null,
      title: columns.title ? cleanCell(row[columns.title]) : '',
    }))
    .filter((row) => row.usOrderId || row.trackingNumber || row.asin);

  return groupProductRows(rows);
}

function rowsFromPaste(text) {
  const parsed = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[\t,;|]+/).map((part) => part.trim());
      return {
        usOrderId: normalizeUsOrderId(parts[0] || ''),
        trackingNumber: cleanCell(parts[1] || ''),
        carrierName: cleanCell(parts[2] || ''),
        asin: cleanCell(parts[3] || '').toUpperCase(),
        itemQuantity: parseItemQuantity(parts[4] || ''),
        purchasePrice: parsePurchasePrice(parts[5] || ''),
        title: cleanCell(parts[6] || ''),
      };
    })
    .filter((row, index) => {
      const orderKey = normalizeHeader(row.usOrderId);
      const trackKey = normalizeHeader(row.trackingNumber);
      if (
        index === 0 &&
        (ORDER_ALIASES.includes(orderKey) || orderKey === 'order') &&
        (TRACKING_ALIASES.includes(trackKey) || trackKey.includes('track'))
      ) {
        return false;
      }
      return row.usOrderId || row.trackingNumber || row.asin;
    });

  return groupProductRows(parsed);
}

function downloadTemplate() {
  const book = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet([
    ['Order Date', 'Order ID', 'Shipment Status', 'Carrier Tracking #', 'Carrier Name', 'ASIN', 'Title', 'Item Quantity', 'Purchase PPU'],
    [
      '09/22/2026',
      '112-1234567-8901234',
      'Shipped',
      'TBA334837292418',
      'Amazon Logistics',
      'B0000530HU',
      'Example product — download this same report from Amazon Business and upload it',
      1,
      '5.47',
    ],
  ]);
  XLSX.utils.book_append_sheet(book, sheet, 'orders_from_amazon');
  XLSX.writeFile(book, 'Order Tracking Upload Excel.xlsx');
}

function pageSlice(rows, page, pageSize) {
  const total = rows.length;
  const totalPages = total === 0 ? 0 : Math.ceil(total / pageSize);
  const safePage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
  const start = (safePage - 1) * pageSize;
  return {
    safePage,
    totalPages,
    start,
    rows: rows.slice(start, start + pageSize),
  };
}

function formatBatchNames(names) {
  if (!Array.isArray(names) || !names.length) return '—';
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

function mergeBatchInfo(previewRows, resultRows) {
  const byProduct = new Map();
  for (const row of resultRows || []) {
    const key = productKey(row);
    if (!String(row.usOrderId || '').trim()) continue;
    byProduct.set(key, {
      batchIds: Array.isArray(row.batchIds) ? row.batchIds : [],
      batchNames: Array.isArray(row.batchNames) ? row.batchNames : [],
      inSystem: row.status !== 'not_found',
      existingTracking: row.existingTracking || null,
      matchStatus: row.status || null,
      matchMessage: row.message || '',
    });
  }
  return previewRows.map((row) => {
    const match = byProduct.get(productKey(row));
    if (!match) {
      return {
        ...row,
        batchIds: row.batchIds || [],
        batchNames: row.batchNames || [],
        inSystem: row.inSystem,
        existingTracking: row.existingTracking || null,
        matchStatus: row.matchStatus || null,
        matchMessage: row.matchMessage || '',
      };
    }
    return {
      ...row,
      batchIds: match.batchIds,
      batchNames: match.batchNames,
      inSystem: match.inSystem,
      existingTracking: match.existingTracking,
      matchStatus: match.matchStatus,
      matchMessage: match.matchMessage,
    };
  });
}

function formatUploadToast(summary, { dryRun = false } = {}) {
  const updated = summary.updated || 0;
  const lines = summary.linesUpdated || 0;
  const alreadyHad = summary.alreadyExists || 0;
  const notInSystem = summary.notFound || 0;
  const skipped = summary.skipped || 0;
  const mismatch = summary.quantityMismatch || 0;
  const ambiguous = summary.ambiguous || 0;

  if (dryRun) {
    return [
      `${updated} can get tracking`,
      `${alreadyHad} already have tracking`,
      `${notInSystem} not found in Procurement`,
      mismatch ? `${mismatch} quantity mismatch` : null,
      ambiguous ? `${ambiguous} ambiguous` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return [
    updated
      ? `Saved tracking on ${updated} product${updated === 1 ? '' : 's'} (${lines} line${lines === 1 ? '' : 's'})`
      : 'No new tracking saved',
    alreadyHad ? `${alreadyHad} already had tracking` : null,
    notInSystem ? `${notInSystem} not found in Procurement` : null,
    mismatch ? `${mismatch} quantity mismatch` : null,
    ambiguous ? `${ambiguous} ambiguous` : null,
    skipped ? `${skipped} skipped` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function readyLabel(row) {
  if (!row.asin) return 'No ASIN';
  if (!row.trackingNumbers?.length) return 'Missing tracking';
  if (!row.quantityComplete) return 'Bad quantity';
  if (row.matchStatus === 'quantity_mismatch') return 'Qty mismatch';
  if (row.matchStatus === 'ambiguous') return 'Ambiguous';
  if (row.matchStatus === 'not_found') return 'Not in system';
  if (row.matchStatus === 'already_exists') return 'Already there';
  if (row.matchStatus === 'skipped') return 'Skipped';
  if (row.matchStatus === 'updated' || (row.usOrderId && row.asin && row.trackingNumbers?.length)) {
    return 'Yes';
  }
  return 'Missing';
}

function canPaste(row) {
  if (!row.asin || !row.trackingNumbers?.length || !row.quantityComplete) return false;
  return !['quantity_mismatch', 'ambiguous', 'not_found', 'skipped', 'error'].includes(row.matchStatus);
}

function TablePager({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  label = 'rows',
}) {
  if (!total) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
      <p className="text-slate-600">
        Page {totalPages === 0 ? 0 : page} of {totalPages}
        <span className="text-slate-400">
          {' '}
          · {total.toLocaleString('en-IN')} {label}
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-slate-500">
          Rows
          <select
            className={`${inputClassName} h-9 w-[4.5rem] py-1 text-sm`}
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={secondaryButtonClassName}
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Previous
        </button>
        <button
          type="button"
          className={secondaryButtonClassName}
          disabled={totalPages === 0 || page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function UploadTrackingPage({ token }) {
  const [pasteText, setPasteText] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [status, setStatus] = useState({ type: 'ready', message: '' });
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingSummary, setPendingSummary] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [resultPage, setResultPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const readyCount = useMemo(() => previewRows.filter(canPaste).length, [previewRows]);

  const previewBatchCount = useMemo(() => {
    const names = new Set();
    for (const row of previewRows) {
      for (const name of row.batchNames || []) {
        if (name) names.add(name);
      }
    }
    return names.size;
  }, [previewRows]);

  const previewPageData = useMemo(
    () => pageSlice(previewRows, previewPage, pageSize),
    [previewRows, previewPage, pageSize]
  );

  const resultRows = result?.results || [];
  const resultPageData = useMemo(
    () => pageSlice(resultRows, resultPage, pageSize),
    [resultRows, resultPage, pageSize]
  );

  const enrichAndSetRows = async (rows, sourceLabel) => {
    setResult(null);
    setOverrideOpen(false);
    setPendingRows([]);
    setPendingSummary(null);
    setPreviewPage(1);
    setResultPage(1);

    if (!rows.length) {
      setPreviewRows([]);
      setStatus({ type: 'error', message: `No Order ID rows found in ${sourceLabel}.` });
      return;
    }

    const withTracking = rows.filter((row) => row.asin && row.trackingNumbers?.length).length;
    setPreviewRows(rows);
    setStatus({
      type: 'ready',
      message: `${rows.length} product${rows.length === 1 ? '' : 's'} · ${withTracking} with Carrier Tracking # · matching batches…`,
    });

    try {
      const lookupRows = rowsForApi(rows);
      if (!lookupRows.length || !token) return;
      const preview = await uploadTrackingIds(token, lookupRows, {
        overrideExisting: false,
        dryRun: true,
      });
      const enriched = mergeBatchInfo(rows, preview.results || []);
      setPreviewRows(enriched);
      const summary = preview.summary || {};
      const batchNames = Array.isArray(summary.batchNames) ? summary.batchNames : [];
      setStatus({
        type: 'ready',
        message: [
          `${rows.length} product${rows.length === 1 ? '' : 's'}`,
          `${withTracking} with Carrier Tracking #`,
          batchNames.length
            ? `${batchNames.length} procurement batch${batchNames.length === 1 ? '' : 'es'}`
            : '0 matching batches',
          summary.quantityMismatch ? `${summary.quantityMismatch} quantity mismatch` : null,
          summary.ambiguous ? `${summary.ambiguous} ambiguous` : null,
        ]
          .filter(Boolean)
          .join(' · '),
      });
    } catch (error) {
      setStatus({
        type: 'ready',
        message: `${rows.length} product${rows.length === 1 ? '' : 's'} · ${withTracking} with Carrier Tracking # (batch lookup failed: ${error.message})`,
      });
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const book = XLSX.read(buffer, { type: 'array', raw: false, cellText: true });
      let rows = [];
      let lastError = null;
      for (const sheetName of book.SheetNames) {
        try {
          rows = rowsFromSheet(book.Sheets[sheetName]);
          if (rows.length) break;
        } catch (error) {
          lastError = error;
        }
      }
      if (!rows.length) {
        throw lastError || new Error('No Order ID rows found in the workbook.');
      }
      setFileName(file.name);
      setPasteText('');
      await enrichAndSetRows(rows, file.name);
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Unable to read file.' });
    }
  };

  const handlePasteLoad = async () => {
    const rows = rowsFromPaste(pasteText);
    setFileName('');
    await enrichAndSetRows(rows, 'pasted text');
  };

  const applyUpload = async (rows, overrideExisting) => {
    setUploading(true);
    try {
      const payload = await uploadTrackingIds(token, rowsForApi(rows), {
        overrideExisting,
        dryRun: false,
      });
      setResult(payload);
      setPreviewRows((current) => mergeBatchInfo(current, payload.results || []));
      setOverrideOpen(false);
      setPendingRows([]);
      setPendingSummary(null);
      setResultPage(1);
      const summary = payload.summary || {};
      setStatus({
        type: summary.updated ? 'success' : 'error',
        message: formatUploadToast(summary, { dryRun: false }),
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    const rows = previewRows;
    if (!rows.some(canPaste)) {
      setStatus({
        type: 'error',
        message: 'Need at least one Order ID, ASIN, Item Quantity, and Carrier Tracking #.',
      });
      return;
    }

    setUploading(true);
    try {
      const preview = await uploadTrackingIds(token, rowsForApi(rows), {
        overrideExisting: false,
        dryRun: true,
      });
      setPreviewRows((current) => mergeBatchInfo(current, preview.results || []));
      const summary = preview.summary || {};
      const existingCount = Number(summary.wouldOverride || summary.alreadyExists || 0);
      const foundCount = Number(summary.updated || 0);

      setPendingSummary({ ...summary, alreadyExists: existingCount });
      setStatus({
        type: 'ready',
        message: formatUploadToast(
          { ...summary, alreadyExists: existingCount },
          { dryRun: true }
        ),
      });

      if (!foundCount && !existingCount) {
        setResult(preview);
        setStatus({
          type: 'error',
          message: formatUploadToast(
            { ...summary, alreadyExists: existingCount },
            { dryRun: true }
          ),
        });
        return;
      }

      if (existingCount > 0) {
        const pref = readOverridePref();
        if (pref === 'always') {
          await applyUpload(rows, true);
          return;
        }
        if (pref === 'never') {
          await applyUpload(rows, false);
          return;
        }
        setPendingRows(rows);
        setDontAskAgain(false);
        setOverrideOpen(true);
        return;
      }

      await applyUpload(rows, false);
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setUploading(false);
    }
  };

  const confirmOverride = async () => {
    if (dontAskAgain) writeOverridePref('always');
    await applyUpload(pendingRows, true);
  };

  const keepExisting = async () => {
    if (dontAskAgain) writeOverridePref('never');
    await applyUpload(pendingRows, false);
  };

  const existingCount = Number(pendingSummary?.alreadyExists || 0);
  const emptyCount = Number(pendingSummary?.updated || 0);

  return (
    <section className="grid gap-4">
      <PageHeader
        title="Upload Tracking ID"
        subtitle="Amazon Excel is shipped rows only. Match Order ID + ASIN and paste Carrier Tracking # onto those procurement lines; products not shipped yet stay untouched."
        actions={
          <button type="button" className={secondaryButtonClassName} onClick={downloadTemplate}>
            Download format
          </button>
        }
      />

      <StatusBanner status={status} setStatus={setStatus} />

      <article className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">How to upload</h2>
        <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-slate-600">
          <li>
            Download data from Amazon — use the Amazon Business order report Excel (same layout as{' '}
            <span className="font-medium text-slate-800">Order Tracking Upload Excel</span>), or click{' '}
            <span className="font-medium text-slate-800">Download format</span> for a sample. That
            file only includes shipped items.
          </li>
          <li>
            Required columns: <span className="font-mono text-xs">Order ID</span>,{' '}
            <span className="font-mono text-xs">ASIN</span>,{' '}
            <span className="font-mono text-xs">Item Quantity</span>, and{' '}
            <span className="font-mono text-xs">Carrier Tracking #</span>. Optional:{' '}
            <span className="font-mono text-xs">Carrier Name</span> and{' '}
            <span className="font-mono text-xs">Purchase PPU</span>.
          </li>
          <li>
            Match by Order ID + ASIN only. If the same US order still has other products in CRM that
            are not shipped, those ASINs are missing from Excel on purpose — no tracking is added
            for them. Re-upload when they ship.
          </li>
          <li>
            If only part of one ASIN has shipped, tracking is pasted onto the shipped quantity and
            the rest of that ASIN is left blank until a later report. When the same Order ID + ASIN
            has several tracking numbers and CRM lines look identical, each TBA is assigned to a
            line by quantity (price is used first when it differs). Only unsplittable cases stay
            manual.
          </li>
        </ol>
      </article>

      <article className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">1. Load Amazon Excel</h2>
        <p className="mt-1 text-sm text-slate-500">
          Upload the Amazon export as-is. Item rows are grouped by Order ID and ASIN, and Item Quantity is summed.
        </p>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <label className="grid gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5">
            <span className="text-sm font-semibold text-slate-800">Upload Excel / CSV</span>
            <span className="text-xs text-slate-500">
              {fileName || 'Amazon Business order report · .xlsx / .xls / .csv'}
            </span>
            <input
              type="file"
              accept=".xlsx,.xls,.csv,text/csv"
              className="text-sm"
              onChange={handleFile}
            />
          </label>

          <div className="grid gap-2">
            <label className="text-sm font-semibold text-slate-800" htmlFor="tracking-paste">
              Or paste Order ID, Tracking, Carrier, ASIN, Quantity
            </label>
            <textarea
              id="tracking-paste"
              className={`${inputClassName} min-h-[8rem] font-mono text-xs`}
              placeholder={'112-1234567-8901234\tTBA334837292418\tAmazon Logistics\tB0000530HU\t1'}
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
            />
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={handlePasteLoad}
              disabled={!pasteText.trim()}
            >
              Load pasted rows
            </button>
          </div>
        </div>
      </article>

      {previewRows.length ? (
        <article className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">2. Preview by Order ID and ASIN</h2>
              <p className="mt-1 text-sm text-slate-500">
                {readyCount} of {previewRows.length} product{previewRows.length === 1 ? '' : 's'} ready
                to paste tracking
                {fileName ? ` · ${fileName}` : ''}
                {previewBatchCount
                  ? ` · ${previewBatchCount} batch${previewBatchCount === 1 ? '' : 'es'} matched`
                  : ''}
              </p>
            </div>
            <button
              type="button"
              className={primaryButtonClassName}
              disabled={uploading || !readyCount}
              onClick={handleUpload}
            >
              {uploading
                ? 'Checking / pasting…'
                : `Paste tracking on ${readyCount} product${readyCount === 1 ? '' : 's'}`}
            </button>
          </div>

          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[56rem] border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    #
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Order ID
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    ASIN
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Qty
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Batch
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Carrier Tracking #
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Carrier Name
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Ready
                  </th>
                </tr>
              </thead>
              <tbody>
                {previewPageData.rows.map((row, index) => {
                  const label = readyLabel(row);
                  const ready = label === 'Yes' || label === 'Already there';
                  const rowNumber = previewPageData.start + index + 1;
                  return (
                    <tr key={`${row.usOrderId}-${row.asin}-${rowNumber}`} className="border-t border-slate-100">
                      <td className={tableCellClassName}>{rowNumber}</td>
                      <td className={`${tableCellClassName} font-mono text-xs`}>
                        {row.usOrderId || '—'}
                      </td>
                      <td className={`${tableCellClassName} font-mono text-xs`}>
                        {row.asin || '—'}
                      </td>
                      <td className={tableCellClassName}>
                        {row.quantityComplete ? row.itemQuantity : '—'}
                      </td>
                      <td className={`${tableCellClassName} text-xs`}>
                        {row.batchNames?.length ? (
                          <span className="text-slate-800">{formatBatchNames(row.batchNames)}</span>
                        ) : row.inSystem === false ? (
                          <span className="text-rose-700">Not in system</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className={`${tableCellClassName} font-mono text-xs`}>
                        {row.trackingNumber || (
                          <span className="text-amber-700">Empty in Amazon file</span>
                        )}
                      </td>
                      <td className={tableCellClassName}>{row.carrierName || '—'}</td>
                      <td className={tableCellClassName}>
                        <span className={ready ? 'text-emerald-700' : 'text-amber-700'}>{label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <TablePager
            page={previewPageData.safePage}
            totalPages={previewPageData.totalPages}
            total={previewRows.length}
            pageSize={pageSize}
            label="products"
            onPageChange={setPreviewPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPreviewPage(1);
              setResultPage(1);
            }}
          />
        </article>
      ) : (
        <EmptyState
          title="No Amazon tracking file loaded"
          description="Download the order report from Amazon Business, or Download format, then upload it here."
        />
      )}

      {resultRows.length ? (
        <article className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">3. Results</h2>
          <p className="mt-1 text-sm text-slate-500">
            Tracking is pasted only when the US Order ID and ASIN match procurement, and the quantities add up.
            India and US ASINs on the same line both count. Already-filled tracking is kept unless you override.
            Quantity mismatches and identical split shipments are left blank for manual paste.
            {result?.summary?.batchNames?.length
              ? ` Matched batches: ${result.summary.batchNames.join(', ')}.`
              : ''}
          </p>
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[64rem] border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    #
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Order ID
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    ASIN
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Qty
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Batch
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Carrier Tracking #
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Already in system
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Status
                  </th>
                  <th className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>
                    Message
                  </th>
                </tr>
              </thead>
              <tbody>
                {resultPageData.rows.map((row, index) => (
                  <tr key={`${row.row}-${row.usOrderId}-${row.asin || 'none'}`} className="border-t border-slate-100">
                    <td className={tableCellClassName}>{resultPageData.start + index + 1}</td>
                    <td className={`${tableCellClassName} font-mono text-xs`}>
                      {row.usOrderId || '—'}
                    </td>
                    <td className={`${tableCellClassName} font-mono text-xs`}>
                      {row.asin || '—'}
                    </td>
                    <td className={tableCellClassName}>{row.itemQuantity || '—'}</td>
                    <td className={`${tableCellClassName} text-xs`}>
                      {row.batchNames?.length ? (
                        formatBatchNames(row.batchNames)
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className={`${tableCellClassName} font-mono text-xs`}>
                      {row.trackingNumber || '—'}
                    </td>
                    <td className={`${tableCellClassName} font-mono text-xs`}>
                      {row.existingTracking || '—'}
                    </td>
                    <td className={tableCellClassName}>
                      <span
                        className={
                          row.status === 'updated'
                            ? 'font-semibold text-emerald-700'
                            : row.status === 'not_found' || row.status === 'error'
                              ? 'font-semibold text-rose-700'
                              : 'font-semibold text-amber-700'
                        }
                      >
                        {row.status === 'already_exists'
                          ? 'already there'
                          : row.status === 'quantity_mismatch'
                            ? 'qty mismatch'
                            : row.status === 'not_found'
                              ? 'not found'
                              : row.status}
                      </span>
                    </td>
                    <td className={tableCellClassName}>{row.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePager
            page={resultPageData.safePage}
            totalPages={resultPageData.totalPages}
            total={resultRows.length}
            pageSize={pageSize}
            label="results"
            onPageChange={setResultPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPreviewPage(1);
              setResultPage(1);
            }}
          />
        </article>
      ) : null}

      {overrideOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed inset-0 z-[200] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
              role="presentation"
              onClick={uploading ? undefined : () => setOverrideOpen(false)}
            >
              <div
                className="crm-panel w-full max-w-md overflow-hidden shadow-2xl"
                role="dialog"
                aria-modal="true"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="px-6 pb-4 pt-6">
                  <h2 className="text-lg font-semibold text-slate-900">
                    Tracking is already there. Do you want to override?
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {existingCount} product{existingCount === 1 ? '' : 's'} already have tracking in
                    Procurement.
                    {emptyCount
                      ? ` ${emptyCount} can be pasted without overriding.`
                      : ''}{' '}
                    Override replaces the existing Carrier Tracking #.
                  </p>
                  <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={dontAskAgain}
                      onChange={(event) => setDontAskAgain(event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    Don&apos;t ask this again
                  </label>
                </div>
                <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
                  <button
                    type="button"
                    className={secondaryButtonClassName}
                    disabled={uploading}
                    onClick={keepExisting}
                  >
                    Keep existing
                  </button>
                  <button
                    type="button"
                    className={primaryButtonClassName}
                    disabled={uploading}
                    onClick={confirmOverride}
                  >
                    {uploading ? 'Updating…' : 'Override'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
}

export default UploadTrackingPage;
